import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSupportNotifications } from '../context/SupportNotificationsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft, User, Clock, Send, Loader2, RefreshCw,
  AlertTriangle, MessageSquare, CheckCircle,
  Paperclip, X, FileText, Image, File, Clipboard,
  Pencil, Trash2, Check
} from 'lucide-react';
import { ImageLightbox } from '../components/ui/image-lightbox';
import { format, formatDistanceToNow } from 'date-fns';

const TenantTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user, subscriptionStatus } = useAuth();
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const isSuspended = subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled';
  const backPath = isSuspended ? '/support' : (location.state?.from === 'settings' ? '/settings?tab=support' : '/support');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const { markRead: markSupportTicketRead } = useSupportNotifications();
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const res = await api.get(`/support/tickets/${ticketId}`);
        setTicket(res.data);
        // Mark as read so the notification badge clears
        markSupportTicketRead(ticketId);
      } catch (err) {
        console.error('Failed to fetch ticket:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, ticketId]);

  // Handle paste event for images
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        if (file.size > 5 * 1024 * 1024) {
          alert('Pasted image is too large. Maximum size is 5MB.');
          continue;
        }

        setUploading(true);
        try {
          const reader = new FileReader();
          const dataUrl = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
          setAttachments(prev => [...prev, {
            filename: `pasted-image-${timestamp}.png`,
            url: dataUrl,
            content_type: file.type,
            size: file.size,
            isPasted: true
          }]);
        } catch (err) {
          console.error('Failed to read pasted image:', err);
        } finally {
          setUploading(false);
        }
      }
    }
  }, []);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newAttachments = [];
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }
      
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newAttachments.push({
        filename: file.name,
        url: dataUrl,
        content_type: file.type,
        size: file.size
      });
    }
    
    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf') || contentType?.includes('document')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const addComment = async (reopen = false) => {
    if (!newComment.trim() && attachments.length === 0) return;
    setSendingComment(true);
    try {
      await api.post(`/support/tickets/${ticketId}/comments`, {
        content: newComment,
        attachments: attachments.length > 0 ? attachments : null,
        reopen
      });
      // Refresh ticket
      const res = await api.get(`/support/tickets/${ticketId}`);
      setTicket(res.data);
      setNewComment('');
      setAttachments([]);
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState(null);

  const startEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
  };
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditContent('');
  };
  const saveEditComment = async (commentId) => {
    if (!editContent.trim()) return;
    setSavingEdit(true);
    try {
      await api.put(`/support/tickets/${ticketId}/comments/${commentId}`, { content: editContent });
      const res = await api.get(`/support/tickets/${ticketId}`);
      setTicket(res.data);
      cancelEditComment();
    } catch (err) {
      console.error('Failed to edit comment:', err);
    } finally {
      setSavingEdit(false);
    }
  };
  const deleteComment = async () => {
    if (!deleteCommentId) return;
    try {
      await api.delete(`/support/tickets/${ticketId}/comments/${deleteCommentId}`);
      const res = await api.get(`/support/tickets/${ticketId}`);
      setTicket(res.data);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setDeleteCommentId(null);
    }
  };

  // Intercept submit on a resolved/closed ticket — prompt the user to reopen
  const handleSubmitComment = () => {
    if (!newComment.trim() && attachments.length === 0) return;
    if (ticket?.status === 'resolved' || ticket?.status === 'closed') {
      setReopenDialogOpen(true);
      return;
    }
    addComment(false);
  };

  const refreshTicket = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await api.get(`/support/tickets/${ticketId}`);
      setTicket(res.data);
    } catch (err) {
      console.error('Failed to refresh ticket:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-purple-500/20 text-purple-600 dark:text-purple-400 hover:bg-purple-500/30 border-transparent';
      case 'open': return 'bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-500/30 border-transparent';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/30 border-transparent';
      case 'waiting_on_customer': return 'bg-orange-500/20 text-orange-600 dark:text-orange-400 hover:bg-orange-500/30 border-transparent';
      case 'waiting_on_support': return 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/30 border-transparent';
      case 'resolved': return 'bg-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-500/30 border-transparent';
      case 'closed': return 'bg-gray-500/20 text-gray-600 dark:text-gray-400 hover:bg-gray-500/30 border-transparent';
      case 'reopened_by_customer': return 'bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-500/30 border-transparent';
      default: return 'bg-gray-500/20 text-gray-600 dark:text-gray-400 hover:bg-gray-500/30 border-transparent';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'waiting_on_customer':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'waiting_on_support':
        return <Clock className="h-5 w-5 text-cyan-500" />;
      default:
        return <Clock className="h-5 w-5 text-blue-500" />;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      general: 'General',
      billing: 'Billing',
      technical: 'Technical',
      feature_request: 'Feature Request',
      bug_report: 'Bug Report'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Ticket not found</p>
        <Button variant="ghost" onClick={() => navigate(backPath)} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Support
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(backPath)}
          data-testid="back-to-support-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-primary font-mono font-medium">{ticket.ticket_number}</span>
            <Badge className={getStatusColor(ticket.status)}>
              {ticket.status?.replace(/_/g, ' ')}
            </Badge>
            <Badge variant="outline">{getCategoryLabel(ticket.category)}</Badge>
          </div>
          <h1 className="text-2xl font-bold">{ticket.subject}</h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" /> {ticket.submitted_by_name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" /> {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refreshTicket}
            disabled={refreshing}
            data-testid="ticket-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {getStatusIcon(ticket.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-muted-foreground">{ticket.description}</p>
              
              {/* Ticket Attachments */}
              {ticket.attachments && ticket.attachments.length > 0 && (
                <div className="mt-4 pt-4 border-t space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Attachments:</p>
                  {ticket.attachments.map((file, idx) => {
                    const FileIcon = getFileIcon(file.content_type);
                    const isImage = file.content_type?.startsWith('image/');
                    return (
                      <div key={`comment-${idx}`}>
                        {isImage ? (
                          <button 
                            type="button"
                            onClick={() => setLightboxUrl(file.url)}
                            className="block cursor-zoom-in"
                            data-testid={`ticket-attachment-img-btn-${idx}`}
                          >
                            <img 
                              src={file.url} 
                              alt={file.filename}
                              className="max-w-sm rounded-lg border hover:opacity-90 transition-opacity"
                            />
                          </button>
                        ) : (
                          <a
                            href={file.url}
                            download={file.filename}
                            className="inline-flex items-center gap-2 p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                          >
                            <FileIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{file.filename}</span>
                            <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" /> Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticket.comments?.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground">No comments yet</p>
              ) : (
                <div className="space-y-4">
                  {ticket.comments?.map((comment) => (
                    <div
                      key={comment.id}
                      className={`p-4 rounded-lg ${
                        comment.author_type === 'support'
                          ? 'bg-primary/10 border border-primary/30'
                          : 'bg-secondary'
                      }`}
                      data-testid={`comment-${comment.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{comment.author_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {comment.author_type === 'support' ? 'Support Agent' : 'You'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(comment.created_at), 'PPp')}
                            {comment.edited_at && <span className="italic ml-1">(edited)</span>}
                          </span>
                          {comment.author_type === 'tenant' && comment.author_id === user?.id && editingCommentId !== comment.id && (
                            <>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => startEditComment(comment)} data-testid={`edit-comment-${comment.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={() => setDeleteCommentId(comment.id)} data-testid={`delete-comment-${comment.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      {editingCommentId === comment.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={3}
                            data-testid={`edit-comment-textarea-${comment.id}`}
                          />
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={() => saveEditComment(comment.id)} disabled={savingEdit || !editContent.trim()} data-testid={`save-comment-${comment.id}`}>
                              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelEditComment} data-testid={`cancel-edit-comment-${comment.id}`}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{comment.content}</p>
                      )}
                      
                      {/* Comment Attachments - show inline images */}
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {comment.attachments.map((file, idx) => {
                            const FileIcon = getFileIcon(file.content_type);
                            const isImage = file.content_type?.startsWith('image/');
                            return (
                              <div key={`comment-${idx}`}>
                                {isImage ? (
                                  <button 
                                    type="button"
                                    onClick={() => setLightboxUrl(file.url)}
                                    className="block cursor-zoom-in"
                                    data-testid={`comment-attachment-img-btn-${idx}`}
                                  >
                                    <img 
                                      src={file.url} 
                                      alt={file.filename}
                                      className="max-w-md rounded-lg border hover:opacity-90 transition-opacity"
                                    />
                                  </button>
                                ) : (
                                  <a
                                    href={file.url}
                                    download={file.filename}
                                    className="inline-flex items-center gap-2 p-2 rounded-lg bg-background hover:bg-background/80 transition-colors border"
                                  >
                                    <FileIcon className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm">{file.filename}</span>
                                    <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Comment - only if ticket not closed */}
              {ticket.status !== 'closed' && (
                <div className="pt-4 border-t">
                  <div className="relative">
                    <Textarea
                      ref={textareaRef}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onPaste={handlePaste}
                      placeholder="Add a comment... (Tip: You can paste screenshots directly!)"
                      className="mb-3 pr-10"
                      rows={4}
                      data-testid="comment-textarea"
                    />
                    <div className="absolute right-3 top-3">
                      <Clipboard className="h-4 w-4 text-muted-foreground" title="Paste images with Ctrl+V" />
                    </div>
                  </div>
                  
                  {/* Attachments Preview - show pasted images inline */}
                  {attachments.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {attachments.map((file, index) => {
                        const FileIcon = getFileIcon(file.content_type);
                        const isImage = file.content_type?.startsWith('image/');
                        return (
                          <div 
                            key={`attachment-${index}`}
                            className="relative"
                          >
                            {isImage ? (
                              <div className="relative inline-block">
                                <img 
                                  src={file.url} 
                                  alt={file.filename}
                                  className="max-w-xs rounded-lg border"
                                />
                                <button
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                                {file.isPasted && (
                                  <span className="absolute bottom-2 left-2 text-xs bg-black/50 text-white px-2 py-1 rounded">
                                    Pasted image
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 p-2 rounded-lg bg-secondary">
                                <FileIcon className="h-5 w-5 text-muted-foreground" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm truncate">{file.filename}</p>
                                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                                </div>
                                <button
                                  onClick={() => removeAttachment(index)}
                                  className="p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        className="hidden"
                        multiple
                        accept="image/*,.pdf,.doc,.docx"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="attach-file-btn"
                      >
                        {uploading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Paperclip className="h-4 w-4 mr-2" />
                        )}
                        Attach File
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        or paste an image (Ctrl+V)
                      </span>
                    </div>
                    <Button
                      onClick={handleSubmitComment}
                      disabled={sendingComment || (!newComment.trim() && attachments.length === 0)}
                      data-testid="send-comment-btn"
                    >
                      {sendingComment ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send
                    </Button>
                  </div>
                </div>
              )}

              {/* Closed ticket notice */}
              {ticket.status === 'closed' && (
                <div className="pt-4 border-t">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted text-muted-foreground">
                    <CheckCircle className="h-5 w-5" />
                    <span>This ticket has been closed. If you need further assistance, please open a new ticket.</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ticket Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusIcon(ticket.status)}
                  <span className="font-medium capitalize">{ticket.status?.replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Category</p>
                <p className="font-medium mt-1">{getCategoryLabel(ticket.category)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-medium mt-1">{format(new Date(ticket.created_at), 'PPP')}</p>
                <p className="text-xs text-muted-foreground">{format(new Date(ticket.created_at), 'p')}</p>
              </div>
              {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <p className="font-medium mt-1">{formatDistanceToNow(new Date(ticket.updated_at), { addSuffix: true })}</p>
                </div>
              )}
              {ticket.resolved_at && (
                <div>
                  <p className="text-sm text-muted-foreground">Resolved</p>
                  <p className="font-medium mt-1">{format(new Date(ticket.resolved_at), 'PPP')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status explanation */}
          {ticket.status === 'waiting_on_customer' && (
            <Card className="border-orange-500/30 bg-orange-500/5">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-600 dark:text-orange-400">Action Required</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Our support team is waiting for your response. Please reply to this ticket to continue.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Reopen confirmation dialog */}
      <AlertDialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
        <AlertDialogContent data-testid="reopen-ticket-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen this ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              This ticket is currently marked as <strong>{ticket.status?.replace(/_/g, ' ')}</strong>.
              Would you like to reopen it so our support team can continue helping you,
              or just add a note without reopening?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setReopenDialogOpen(false)}
              data-testid="reopen-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                setReopenDialogOpen(false);
                addComment(false);
              }}
              data-testid="reopen-no-btn"
            >
              Just add note
            </Button>
            <AlertDialogAction
              onClick={() => {
                setReopenDialogOpen(false);
                addComment(true);
              }}
              data-testid="reopen-confirm-btn"
            >
              Yes, reopen ticket
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete comment confirmation dialog */}
      <AlertDialog open={!!deleteCommentId} onOpenChange={(open) => !open && setDeleteCommentId(null)}>
        <AlertDialogContent data-testid="delete-comment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this comment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove your comment. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-comment-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteComment} data-testid="delete-comment-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
};

export default TenantTicketDetail;
